import type { OpozoriloUvoza } from '@koda/shared';
import type { JSX } from 'react';

/**
 * Opozorila, ki gredo med zložene podrobnosti.
 *
 * Neuporabljeni stolpci so informativni: izvoz jih ima pogosto na desetine,
 * ukrepati pa ni treba nič. Enakovredno z ostalimi opozorili bi zakrivali
 * tisto, kar je res treba pogledati (docs/ARHITEKTURA.md, 5.5).
 */
const PODROBNOSTI = new Set(['dodatni_stolpci', 'prezrti_stolpci']);

/** Sklanjatev ob številu: 1 stolpec, 2 stolpca, 3 stolpci, 5 stolpcev. */
function neuporabljenih(n: number): string {
  const ostanek = n % 100;
  if (ostanek === 1) return 'neuporabljen stolpec';
  if (ostanek === 2) return 'neuporabljena stolpca';
  if (ostanek === 3 || ostanek === 4) return 'neuporabljeni stolpci';
  return 'neuporabljenih stolpcev';
}

interface Lastnosti {
  opozorila: OpozoriloUvoza[];
}

/**
 * Izid kontrol ob uvozu.
 *
 * Pomembna opozorila — neizravnana glavna knjiga, konti brez skupine,
 * sprememba glave — so vidna takoj; informativna so zložena, da jih ne
 * zakrijejo. Napaka pomeni, da uvoz ni bil izveden; opozorilo ne.
 */
export function OpozorilaUvoza({ opozorila }: Lastnosti): JSX.Element {
  const glavna = opozorila.filter((o) => !PODROBNOSTI.has(o.vrsta));
  const podrobna = opozorila.filter((o) => PODROBNOSTI.has(o.vrsta));
  const stolpcev = podrobna.reduce((n, o) => n + (o.podrobnosti?.length ?? 0), 0);

  return (
    <>
      {glavna.length > 0 && (
        <ul className="opozorila">
          {glavna.map((o) => (
            <li key={o.vrsta} className={o.napaka ? 'napaka-vrstica' : 'opozorilo-vrstica'}>
              <strong>{o.napaka ? 'Napaka' : 'Opozorilo'}:</strong> {o.sporocilo}
            </li>
          ))}
        </ul>
      )}
      {podrobna.length > 0 && (
        <details className="podrobnosti-uvoza">
          <summary>
            Podrobnosti uvoza
            {stolpcev > 0 ? ` (${String(stolpcev)} ${neuporabljenih(stolpcev)})` : ''}
          </summary>
          <ul className="opozorila">
            {podrobna.map((o) => (
              <li key={o.vrsta} className="opozorilo-vrstica">
                {o.sporocilo}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
